/*       */

// $FlowFixMe
import React, { useState, useCallback, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { TextField, ListItem, Typography, Collapse, List } from '@mic3/platform-ui';
import Icon from 'app/components/atoms/Icon/Icon';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import { getFieldDefinition } from 'app/utils/designer/form/settings/formFieldSettingsUtils';
import { formFieldCategories, getFieldMetadata, getCategoryName } from 'app/utils/designer/form/settings/formFieldMetadata';
import { focusInput } from 'app/utils/utils';

const SearchFieldWrapper = styled.div`
    padding: 0 16px 8px;
    position: sticky;
    top: -16px;
    z-index: 99;
    background: ${({ theme }) => theme.material.colors.background.paper};
`;

const ListItemStyled = styled(ListItem)`
    cursor: pointer;
`;

const CategoryHeader = styled(ListItem)`
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 16px 16px;
    gap: 16px;
`;

const CategoryTitle = styled(Typography)`
    font-family: Roboto;
    font-weight: 500;
    font-style: Medium;
    font-size: 10px;
    leading-trim: NONE;
    line-height: 16px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    flex-grow: 1;
    color: ${({ theme }) => theme.material.colors.text.primary};
`;

const CategoryCount = styled.span`
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const ComponentName = styled(Typography)`
    line-height: 1.2 !important;
`;

const ComponentDescription = styled(Typography)`
    color: ${({ theme }) => theme.material.colors.text.secondary};
    font-size: 0.85rem;
    line-height: 1.2 !important;
`;

const ComponentInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin-top: 8px;
    margin-bottom: 8px;
`;

const ComponentIcon = styled(Icon)`
    margin-right: 16px;
    font-size: 24px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const ExpandIcon = styled(Icon)`
    transition: transform 0.2s;
    transform: ${({ expanded }) => (expanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
    color: ${({ theme }) => theme.material.colors.text.secondary};
`;

const CategoryDivider = styled.div`
    height: 1px;
    margin: 8px 16px;
    background-color: rgba(255, 255, 255, 0.12);
`;

const CategoryWrapper = styled.div``;

const FieldsModal = ({ elements, modal, showModal, toggleModal, onAdd, toggleIotModal, isMobile }) => {
    const [search, setSearch] = useState('');
    const [expandedCategories, setExpandedCategories] = useState(() => {
        // Start with all categories collapsed
        return formFieldCategories.reduce((acc, cat) => {
            acc[cat.id] = false;
            return acc;
        }, {});
    });

    const onSearch = useCallback(event => {
        setSearch(event.target.value);
    }, []);

    const toggleCategory = useCallback(categoryId => {
        setExpandedCategories(prev => ({
            ...prev,
            [categoryId]: !prev[categoryId],
        }));
    }, []);

    const onChange = useCallback(type => () => {
        const { index, parentUuid, isRoot } = showModal;
        if (type === 'iotPanel') {
            toggleModal();
            toggleIotModal();
            return;
        }
        const el = getFieldDefinition(type);
        onAdd(el, parentUuid, index + 1, isRoot);
        toggleModal();
    }, [showModal, toggleModal, toggleIotModal, onAdd]);

    // Group elements by category and filter by search
    const groupedElements = useMemo(() => {
        const searchTerm = search || '';
        const searchLower = searchTerm.toLowerCase();

        // Filter elements by search (match against friendly name or description)
        const filteredElements = elements.filter(({ type }) => {
            if (!searchTerm) return true;
            const metadata = getFieldMetadata(type);
            return (
                metadata.name.toLowerCase().includes(searchLower) ||
                metadata.description.toLowerCase().includes(searchLower)
            );
        });

        // Group by category
        const groups = {};
        filteredElements.forEach(({ type }) => {
            const metadata = getFieldMetadata(type);
            const categoryId = metadata.category;
            if (!groups[categoryId]) {
                groups[categoryId] = [];
            }
            groups[categoryId].push({ type, metadata });
        });

        // Sort categories by order and return as array
        return formFieldCategories
            .filter(cat => groups[cat.id] && groups[cat.id].length > 0)
            .map(cat => ({
                ...cat,
                items: groups[cat.id],
            }));
    }, [elements, search]);

    const componentsList = useMemo(() =>
        groupedElements.map((category, index) => (
            <CategoryWrapper key={category.id}>
                {index > 0 && <CategoryDivider />}
                <CategoryHeader dense ContainerComponent="div" onClick={() => toggleCategory(category.id)}>
                    <ExpandIcon name="menu-down" expanded={expandedCategories[category.id] || !!search} />
                    <CategoryTitle variant="caption">
                        {getCategoryName(category.id)} <CategoryCount>({category.items.length})</CategoryCount>
                    </CategoryTitle>
                </CategoryHeader>
                <Collapse in={expandedCategories[category.id] || !!search}>
                    <List disablePadding>
                        {category.items.map(({ type, metadata }) => (
                            <ListItemStyled key={type} ContainerComponent="div" onClick={onChange(type)}>
                                <ComponentIcon name={metadata.icon} type={metadata.iconType} size="md" />
                                <ComponentInfo>
                                    <ComponentName variant="subtitle1">{metadata.name}</ComponentName>
                                    <ComponentDescription variant="body2">{metadata.description}</ComponentDescription>
                                </ComponentInfo>
                            </ListItemStyled>
                        ))}
                    </List>
                </Collapse>
            </CategoryWrapper>
        ))
    , [groupedElements, expandedCategories, onChange, toggleCategory, search]);

    return (
        !!showModal && (
            <ModalDialog title="Form elements" onClose={() => toggleModal()} dialogProps={{ maxWidth: 'sm' }}>
                <SearchFieldWrapper>
                    <TextField placeholder="Search..." value={search} onChange={onSearch} variant="standard" inputRef={(ref) => !isMobile && focusInput(ref)} />
                </SearchFieldWrapper>
                {componentsList}
            </ModalDialog>
        )
    );
};

FieldsModal.propTypes = {
    elements: PropTypes.array,
    modal: PropTypes.any,
    showModal: PropTypes.any,
    toggleModal: PropTypes.func.isRequired,
    onAdd: PropTypes.func.isRequired,
    toggleIotModal: PropTypes.func.isRequired,
    isMobile: PropTypes.bool,
};

export default memo(FieldsModal);
